import { resolveImage } from "@/lib/images";

/**
 * A responsive image, with no inline style.
 *
 * The name is a leftover with a live reason behind it. This wrapped
 * `next/image` originally, whose output carries an inline `style` attribute
 * that the portal's `style-src` nonce cannot authorise — so the component
 * existed to strip it. `next/image` is gone, and with it sharp and libvips, but
 * the constraint has not moved: nothing here may emit an inline style.
 *
 * Sizing is done with attributes and CSS instead. `width` and `height` are the
 * intrinsic dimensions, which is what lets the browser reserve the box before
 * the bytes arrive; the actual displayed size comes from the stylesheet.
 */
type CspImageProps = {
  src: string;
  /** Empty for a decorative image, which is what the logo is beside its wordmark. */
  alt: string;
  width: number;
  height: number;
  className?: string;
  loading?: "eager" | "lazy";
  fetchPriority?: "high" | "low" | "auto";
  /** Layout hint for `srcset` selection, e.g. `"(min-width: 640px) 512px, 60vw"`. */
  sizes?: string;
};

export function CspImage({
  src,
  alt,
  width,
  height,
  className,
  loading = "lazy",
  fetchPriority,
  sizes,
}: CspImageProps) {
  const resolved = resolveImage(src, width);

  return (
    <img
      src={resolved.src}
      srcSet={resolved.srcSet}
      sizes={sizes ?? `${width}px`}
      alt={alt}
      width={width}
      height={height}
      className={className}
      loading={loading}
      fetchPriority={fetchPriority}
      decoding="async"
    />
  );
}
